import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import NavBar from '../components/NavBar';

declare global {
  interface Window {
    grecaptcha: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}

const ADMIN_API = process.env.REACT_APP_ADMIN_API_URL || 'http://localhost:5000';
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

type Status = 'idle' | 'sending' | 'success' | 'error' | 'spam' | 'unavailable';

const navStyle = {
  color: '#9e9e9e',
  fontFamily: '"Inter", sans-serif',
  fontSize: '0.8rem',
  letterSpacing: '0.15em',
  textTransform: 'uppercase' as const,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  p: 0,
  '&:hover': { color: '#f0ede8' },
  transition: 'color 0.2s',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#f0ede8',
    fontFamily: '"Inter", sans-serif',
    '& fieldset': { borderColor: 'rgba(240,237,232,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(240,237,232,0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#c9a84c' },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(240,237,232,0.5)',
    fontFamily: '"Inter", sans-serif',
    '&.Mui-focused': { color: '#c9a84c' },
  },
};

const ContactPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<Status>('idle');

  const getRecaptchaToken = (): Promise<string> => {
    if (!RECAPTCHA_SITE_KEY || !window.grecaptcha) return Promise.resolve('');
    return new Promise((resolve) => {
      window.grecaptcha.ready(async () => {
        const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'contact' });
        resolve(token);
      });
    });
  };

  const submit = async () => {
    setStatus('sending');
    try {
      const recaptchaToken = await getRecaptchaToken();
      const res = await fetch(`${ADMIN_API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message, recaptchaToken, website: '' }),
      });

      if (res.ok) {
        setStatus('success');
      } else {
        const data = await res.json().catch(() => ({}));
        const errorMsg = (data.error || '').toLowerCase();
        if (errorMsg.includes('spam') || errorMsg.includes('bot')) {
          setStatus('spam');
        } else if (res.status === 503 || errorMsg.includes('unavailable')) {
          setStatus('unavailable');
        } else {
          setStatus('error');
        }
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <Box data-testid="contact-page-root" sx={{ minHeight: '100vh', position: 'relative' }}>
      <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        <Box
          data-testid="contact-bg-image"
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url(/images/202412/skeleton.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.18)',
            transform: 'scale(1.05)',
          }}
        />
      </Box>
      <NavBar />
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 640,
          mx: 'auto',
          pt: { xs: 14, md: 16 },
          pb: { xs: 8, md: 12 },
          px: { xs: 3, md: 4 },
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <Box sx={{ width: 40, height: 2, backgroundColor: '#c9a84c', mb: 3 }} />
          <Typography
            variant="h2"
            sx={{ mb: 2, fontSize: { xs: '2rem', md: '3rem' }, color: '#f0ede8', fontFamily: '"Cormorant Garamond", serif' }}
          >
            Contact
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'rgba(240,237,232,0.6)', fontFamily: '"Inter", sans-serif', mb: 5, lineHeight: 1.8 }}
          >
            Have a question or want to get in touch? Send a message and I'll get back to you.
          </Typography>

          {status === 'success' ? (
            <Box
              data-testid="contact-success"
              sx={{ p: 4, border: '1px solid #c9a84c', borderRadius: 1, textAlign: 'center' }}
            >
              <Typography sx={{ color: '#c9a84c', fontFamily: '"Cormorant Garamond", serif', fontSize: '1.5rem', mb: 1 }}>
                Message sent
              </Typography>
              <Typography sx={{ color: 'rgba(240,237,232,0.7)', fontFamily: '"Inter", sans-serif', fontSize: '0.9rem' }}>
                Thank you for reaching out. I'll reply as soon as possible.
              </Typography>
            </Box>
          ) : (
            <Box component="form" noValidate onSubmit={(e) => { e.preventDefault(); submit(); }}>
              {/* Honeypot — hidden from real users */}
              <input
                type="text"
                name="website"
                aria-hidden="true"
                tabIndex={-1}
                style={{ display: 'none' }}
                readOnly
                value=""
              />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  label="Name"
                  variant="outlined"
                  fullWidth
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  slotProps={{ htmlInput: { 'data-testid': 'contact-name' } }}
                  sx={fieldSx}
                />
                <TextField
                  label="Email"
                  variant="outlined"
                  fullWidth
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  slotProps={{ htmlInput: { 'data-testid': 'contact-email' } }}
                  sx={fieldSx}
                />
                <TextField
                  label="Message"
                  variant="outlined"
                  fullWidth
                  required
                  multiline
                  rows={5}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  slotProps={{ htmlInput: { 'data-testid': 'contact-message' } }}
                  sx={fieldSx}
                />

                {status === 'spam' && (
                  <Box data-testid="contact-spam" sx={{ p: 2, border: '1px solid rgba(200,80,80,0.5)', borderRadius: 1 }}>
                    <Typography sx={{ color: '#e57373', fontFamily: '"Inter", sans-serif', fontSize: '0.85rem' }}>
                      Your message was flagged as spam. Please try again or contact directly by email.
                    </Typography>
                  </Box>
                )}

                {status === 'unavailable' && (
                  <Box data-testid="contact-unavailable" sx={{ p: 2, border: '1px solid rgba(200,80,80,0.5)', borderRadius: 1 }}>
                    <Typography sx={{ color: '#e57373', fontFamily: '"Inter", sans-serif', fontSize: '0.85rem' }}>
                      Verification is temporarily unavailable. Please try again in a moment.
                    </Typography>
                  </Box>
                )}

                {status === 'error' && (
                  <Box data-testid="contact-error" sx={{ p: 2, border: '1px solid rgba(200,80,80,0.5)', borderRadius: 1 }}>
                    <Typography sx={{ color: '#e57373', fontFamily: '"Inter", sans-serif', fontSize: '0.85rem' }}>
                      Something went wrong. Please try again or contact us directly by email.
                    </Typography>
                  </Box>
                )}

                <Button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={status === 'sending' || !name || !email || !message}
                  sx={{
                    alignSelf: 'flex-start',
                    px: 4,
                    py: 1.5,
                    backgroundColor: '#c9a84c',
                    color: '#070b1c',
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '0.8rem',
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    borderRadius: 0,
                    '&:hover': { backgroundColor: '#b8963e' },
                    '&:disabled': { backgroundColor: 'rgba(201,168,76,0.3)', color: 'rgba(7,11,28,0.5)' },
                  }}
                >
                  {status === 'sending' ? <CircularProgress size={18} sx={{ color: '#070b1c' }} /> : 'Send Message'}
                </Button>
              </Box>
            </Box>
          )}
        </motion.div>
      </Box>
    </Box>
  );
};

export default ContactPage;
